import React, { ReactNode, useContext, useState } from 'react';
import { ProColumns } from '@ant-design/pro-components';
import { ColumnType } from 'antd/es/table/interface';

interface Auth {
  authed: boolean;
  permission: string[];
  login: (permission: string[]) => void;
  logout: () => void;
}

const authContext = React.createContext<Auth>({
  authed: false,
  permission: [],
  login: permission => {},
  logout: () => {},
});

function useAuth() {
  const [auth, setAuth] = useState<{
    authed: boolean;
    permission: string[];
  }>({
    authed: false,
    permission: [],
  });
  return {
    authed: auth.authed,
    permission: auth.permission,
    login(permission: string[]) {
      setAuth({
        authed: true,
        permission: [
          ...permission,
        ],
      });
    },
    logout() {
      setAuth({
        authed: false,
        permission: [],
      });
    },
  };
}

export function AuthProvider({ children }: {
  children?: ReactNode;
}) {
  const auth = useAuth();
  return (
    <authContext.Provider value={auth}>
      {children}
    </authContext.Provider>
  );
}

export default function AuthConsumer() {
  return useContext(authContext);
}

export type AuthProColumns<T = any, ValueType = 'text'> = ProColumns<T, ValueType> & {
  permission?: string[];
};

export type AuthColumnType<RecordType> = ColumnType<RecordType> & {
  permission?: string[];
};

function hasAnyPermission(permissions: string[], valid: string[] | undefined) {
  if (valid === undefined) {
    return true;
  }
  for (let i = 0; i < valid.length; i++) {
    if (permissions.includes(valid[i])) {
      return true;
    }
  }
  return false;
}

/**
 * 表格列权限控制
 */
type ResultType<T> = Omit<T, 'permission'>;
export function AuthActionColumn<T extends { permission?: string[] }>(columns: T[]): Array<ResultType<T>> {
  const auth = AuthConsumer();
  const isSuperAdmin = auth.permission.includes('all');
  const result: Array<ResultType<T>> = [];
  for (let i = 0; i < columns.length; i++) {
    const { permission, ...column } = columns[i];
    if (isSuperAdmin || hasAnyPermission(auth.permission, permission)) {
      result.push(column);
    }
  }
  return result;
}

export function hasAny(...permissions: string[]) {
  const auth = AuthConsumer();
  if (auth.permission.includes('all')) {
    return true;
  }
  return hasAnyPermission(auth.permission, permissions);
}
